import { acquirePagedSearchBusy, refreshPagedSearchBusy } from './paged-search-surface-busy.js';
import { swapResultsMarkup } from './paged-search-surface.js';
import {
    acquirePaginationPending,
    acquirePaginationSlowFeedback,
    announcePaginationPosition,
} from './pagination-feedback.js';

// Document -> its quiet reloader; presence also marks the document as initialized.
const reloaders = new WeakMap();

/**
 * Reloads a paginated results region in place for the current URL, through the same request ownership
 * as page clicks and history (a newer load aborts it, and it aborts an older one), without retry UI, slow
 * status, page announcement or navigation. `onCommit(fragment)` runs inside the swap's DOM commit.
 * Resolves to `swapped`, `not-found` (404), `failed`, `stale` (superseded) or `unavailable` (pagination
 * never initialized for this document).
 *
 * @param {Document} documentRef
 * @param {HTMLElement} element carrying `data-pagination-ajax-url` and `data-pagination-ajax-target`
 * @param {(fragment: object) => void} [onCommit]
 */
export async function reloadAjaxPagination(documentRef, element, onCommit = () => {}) {
    const reload = reloaders.get(documentRef);

    return reload === undefined ? 'unavailable' : reload(element, onCommit);
}

export function initAjaxPagination(
    documentRef = globalThis.document,
    windowRef = globalThis.window,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    consoleRef = globalThis.console,
    timerApi = globalThis,
) {
    if (
        documentRef === undefined
        || documentRef === null
        || windowRef === undefined
        || windowRef === null
        || typeof fetchImpl !== 'function'
        || reloaders.has(documentRef)
    ) {
        return;
    }

    const activeRequests = new Map();
    const retryByNav = new WeakMap();

    const invalidate = () => {
        activeRequests.forEach((request) => request.abortController.abort());
    };

    const load = async (nav, publicUrl, historyMode, feedbackTarget = null, interaction = null, quiet = null) => {
        const config = resolvePaginationConfig(documentRef, nav);
        if (config === null) {
            if (quiet === null) {
                navigate(windowRef, publicUrl);
            }
            return 'failed';
        }

        activeRequests.get(config.results)?.abortController.abort();
        const abortController = new AbortController();
        const request = { abortController };
        activeRequests.set(config.results, request);
        if (quiet === null) {
            setPaginationError(nav, false);
        }
        const releaseBusy = acquirePagedSearchBusy(config.root, 'pagination');
        const releasePending = acquirePaginationPending(config.root, feedbackTarget);
        const releaseSlowFeedback = quiet === null ? acquirePaginationSlowFeedback(nav, timerApi) : () => {};
        let status = 0;

        try {
            const requestUrl = new URL(config.ajaxUrl, windowRef.location?.origin ?? publicUrl.origin);
            requestUrl.search = publicUrl.search;
            const response = await fetchImpl(requestUrl.toString(), {
                headers: {
                    Accept: 'text/html',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: abortController.signal,
            });
            status = response.status;
            if (!response.ok) {
                throw new Error(`Pagination request failed with status ${response.status}`);
            }

            const html = await response.text();
            if (!ownsRequest(activeRequests, config.results, request)) {
                return 'stale';
            }

            const fragment = parsePaginationFragment(documentRef, html, config.targetSelector);
            if (quiet !== null && fragment.currentPage === null) {
                // The parser falls back to the whole body when the target is missing; never swap that in.
                throw new Error('Pagination response is missing its results.');
            }
            await swapResultsMarkup(config.results, () => {
                config.results.innerHTML = fragment.markup;
                syncPaginationMetadata(config.results, fragment);
                refreshPagedSearchBusy(config.root);
                quiet?.onCommit(fragment);
            }, {
                windowRef,
                shouldAnimate: true,
                canMutate: () => ownsRequest(activeRequests, config.results, request),
            });

            if (!ownsRequest(activeRequests, config.results, request)) {
                return 'stale';
            }

            const canonicalPublicUrl = resolveCanonicalPublicUrl(
                windowRef.location?.href,
                publicUrl,
                fragment,
            );
            if (historyMode === 'push') {
                windowRef.history?.pushState?.(
                    { ajaxPaginationTarget: config.targetSelector },
                    '',
                    `${canonicalPublicUrl.pathname}${canonicalPublicUrl.search}${canonicalPublicUrl.hash}`,
                );
            } else if (historyMode === 'none' && canonicalPublicUrl.toString() !== publicUrl.toString()) {
                windowRef.history?.replaceState?.(
                    windowRef.history?.state ?? null,
                    '',
                    `${canonicalPublicUrl.pathname}${canonicalPublicUrl.search}${canonicalPublicUrl.hash}`,
                );
            }

            retryByNav.delete(nav);
            if (quiet === null) {
                announcePaginationPosition([config.results, config.root], fragment);
            }
            config.root.dispatchEvent(new CustomEvent('pagination:swap', {
                bubbles: true,
                detail: {
                    results: config.results,
                    adjustViewport: interaction !== null,
                    keyboardActivated: interaction?.keyboardActivated === true,
                    totalItems: fragment.totalItems,
                },
            }));

            return 'swapped';
        } catch (error) {
            if (abortController.signal.aborted) {
                return 'stale';
            }
            if (quiet !== null) {
                return status === 404 ? 'not-found' : 'failed';
            }

            consoleRef?.error?.(error);
            retryByNav.set(nav, { publicUrl, historyMode });
            setPaginationError(nav, true);

            return 'failed';
        } finally {
            releaseSlowFeedback();
            releasePending();
            releaseBusy();
            if (activeRequests.get(config.results) === request) {
                activeRequests.delete(config.results);
            }
        }
    };
    reloaders.set(documentRef, (element, onCommit) => load(
        element,
        new URL(windowRef.location?.href ?? 'http://localhost'),
        'none',
        null,
        null,
        { onCommit },
    ));

    documentRef.addEventListener('click', (event) => {
        if (event.defaultPrevented) {
            return;
        }

        const retry = event.target?.closest?.('[data-pagination-retry]');
        const retryNav = retry?.closest?.('[data-pagination-ajax-url]');
        const retryState = retryNav instanceof HTMLElement ? retryByNav.get(retryNav) : undefined;
        if (retry instanceof HTMLElement && retryNav instanceof HTMLElement && retryState !== undefined) {
            event.preventDefault();

            return load(retryNav, retryState.publicUrl, retryState.historyMode, null, {
                keyboardActivated: event.detail === 0,
            });
        }

        const link = event.target?.closest?.('[data-page-number]');
        const href = link?.getAttribute?.('href');
        const nav = link?.closest?.('[data-pagination-ajax-url]');
        if (!(link instanceof HTMLElement) || !(nav instanceof HTMLElement) || typeof href !== 'string' || href === '') {
            return;
        }

        const targetSelector = String(nav.dataset.paginationAjaxTarget ?? '').trim();
        const results = targetSelector === '' ? null : documentRef.querySelector(targetSelector);
        const pageParam = String(results?.dataset?.paginationPageParam ?? '').trim();
        const destinationPage = normalizePositiveInteger(link.dataset.pageNumber);
        if (!(results instanceof HTMLElement) || pageParam === '' || destinationPage === null) {
            return;
        }

        event.preventDefault();
        const publicUrl = buildPaginationPublicUrl(
            windowRef.location?.href,
            href,
            pageParam,
            destinationPage,
        );

        return load(nav, publicUrl, 'push', link, {
            keyboardActivated: event.detail === 0,
        });
    });

    documentRef.addEventListener('pagination:invalidate', invalidate);
    windowRef.addEventListener?.('popstate', (event) => {
        const publicUrl = new URL(windowRef.location?.href ?? 'http://localhost');
        const beforeLoadEvent = new CustomEvent('pagination:popstate', {
            cancelable: true,
            detail: { publicUrl },
        });
        if (documentRef.dispatchEvent(beforeLoadEvent) === false) {
            return;
        }

        const nav = resolveHistoryPagination(documentRef, event?.state?.ajaxPaginationTarget);
        if (!(nav instanceof HTMLElement)) {
            return;
        }

        void load(nav, publicUrl, 'none');
    });
}

function resolvePaginationConfig(documentRef, nav) {
    const ajaxUrl = String(nav.dataset.paginationAjaxUrl ?? '').trim();
    const targetSelector = String(nav.dataset.paginationAjaxTarget ?? '').trim();
    const results = targetSelector === '' ? null : documentRef.querySelector(targetSelector);
    if (ajaxUrl === '' || !(results instanceof HTMLElement)) {
        return null;
    }

    return {
        ajaxUrl,
        results,
        root: results.closest('[data-pagination-ajax-root]') ?? results,
        targetSelector,
    };
}

function resolveHistoryPagination(documentRef, targetSelector) {
    const navs = Array.from(documentRef.querySelectorAll('[data-pagination-ajax-url]'));
    const sharedNavs = navs.filter((nav) => (
        nav instanceof HTMLElement
        && nav.dataset.paginationAjaxHistoryOwner !== 'custom'
    ));
    const normalizedTarget = typeof targetSelector === 'string' ? targetSelector.trim() : '';

    return sharedNavs.find((nav) => nav.dataset.paginationAjaxTarget === normalizedTarget)
        ?? sharedNavs[0]
        ?? null;
}

export function parsePaginationFragment(documentRef, html, targetSelector) {
    const template = documentRef.createElement?.('template');
    if (template?.content?.querySelector === undefined) {
        return emptyFragment(html);
    }

    template.innerHTML = html.trim();
    const incomingTarget = template.content.querySelector(targetSelector);
    if (!(incomingTarget instanceof HTMLElement)) {
        return emptyFragment(html);
    }

    return {
        markup: incomingTarget.innerHTML,
        currentPage: normalizePositiveInteger(incomingTarget.dataset.paginationCurrentPage),
        totalPages: normalizeNonNegativeInteger(incomingTarget.dataset.paginationTotalPages),
        totalItems: normalizeNonNegativeInteger(incomingTarget.dataset.paginationTotalItems),
        pageParam: String(incomingTarget.dataset.paginationPageParam ?? '').trim(),
        pageLabel: String(incomingTarget.dataset.paginationPageLabel ?? '').trim(),
    };
}

function emptyFragment(markup) {
    return {
        markup,
        currentPage: null,
        totalPages: null,
        totalItems: null,
        pageParam: '',
        pageLabel: '',
    };
}

function ownsRequest(activeRequests, results, request) {
    return activeRequests.get(results) === request
        && !request.abortController.signal.aborted
        && results.isConnected;
}

function buildPaginationPublicUrl(currentHref, destinationHref, pageParam, destinationPage) {
    const destination = new URL(destinationHref, currentHref ?? 'http://localhost');
    const publicUrl = new URL(currentHref ?? destination.toString(), destination.origin);
    publicUrl.pathname = destination.pathname;
    publicUrl.hash = destination.hash;
    applyCanonicalPage(publicUrl, pageParam, destinationPage);

    return publicUrl;
}

function resolveCanonicalPublicUrl(currentHref, publicUrl, fragment) {
    if (fragment.pageParam === '' || fragment.currentPage === null) {
        return new URL(publicUrl.toString());
    }

    const canonicalUrl = new URL(currentHref ?? publicUrl.toString(), publicUrl.origin);
    canonicalUrl.pathname = publicUrl.pathname;
    canonicalUrl.hash = publicUrl.hash;
    applyCanonicalPage(canonicalUrl, fragment.pageParam, fragment.currentPage);

    return canonicalUrl;
}

function applyCanonicalPage(url, pageParam, page) {
    if (pageParam === '' || page === null) {
        return;
    }

    if (page > 1) {
        url.searchParams.set(pageParam, String(page));
    } else {
        url.searchParams.delete(pageParam);
    }
}

function syncPaginationMetadata(results, fragment) {
    if (fragment.currentPage !== null) {
        results.dataset.paginationCurrentPage = String(fragment.currentPage);
    }
    if (fragment.totalPages !== null) {
        results.dataset.paginationTotalPages = String(fragment.totalPages);
    }
    if (fragment.totalItems !== null) {
        results.dataset.paginationTotalItems = String(fragment.totalItems);
    }
    if (fragment.pageParam !== '') {
        results.dataset.paginationPageParam = fragment.pageParam;
    }
    if (fragment.pageLabel !== '') {
        results.dataset.paginationPageLabel = fragment.pageLabel;
    }
}

function setPaginationError(nav, visible) {
    const error = nav.querySelector('[data-pagination-error]');
    if (error instanceof HTMLElement) {
        error.hidden = !visible;
    }
}

function normalizePositiveInteger(value) {
    const normalized = String(value ?? '').trim();

    return /^[1-9]\d*$/.test(normalized) ? Number.parseInt(normalized, 10) : null;
}

function normalizeNonNegativeInteger(value) {
    const normalized = String(value ?? '').trim();

    return /^(0|[1-9]\d*)$/.test(normalized) ? Number.parseInt(normalized, 10) : null;
}

function navigate(windowRef, url) {
    if (typeof windowRef.location?.assign === 'function') {
        windowRef.location.assign(url.toString());
        return;
    }

    if (windowRef.location) {
        windowRef.location.href = url.toString();
    }
}
